import React, {
    useState
} from 'react';
import {
    Popover,
    PopoverTrigger,
    PopoverContent
} from 'shared/components/ui/popover';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Slider
} from 'shared/components/ui/slider';
import {
    Button
} from 'shared/components/ui/button';
import {
    Settings2,
    Info
} from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from 'shared/components/ui/tooltip';

interface GeminiConfigurationParams {
    temperature: number;
    top_p: number;
    top_k: number;
    candidate_count: number;
    max_output_tokens: number;
    stop_sequences: string[];
    presence_penalty: number;
    frequency_penalty: number;
    seed?: number;
}

interface GeminiConfigurationProps {
    parameters: GeminiConfigurationParams;
    setParameters: (params: GeminiConfigurationParams) => void;
}

const GeminiConfiguration: React.FC<GeminiConfigurationProps> = ({
    parameters,
    setParameters
}) => {
    const [tempLimit] = useState({
        min: 0,
        max: 2
    });
    const [topPLimit] = useState({
        min: 0,
        max: 1
    });
    const [topKLimit] = useState({
        min: 1,
        max: 100
    });
    const [candidateCountLimit] = useState({
        min: 1,
        max: 8
    });
    const [frequencyPenaltyLimit] = useState({
        min: -2.0,
        max: 2.0
    });
    const [presencePenaltyLimit] = useState({
        min: -2.0,
        max: 2.0
    });
    const [seedLimit] = useState({
        min: -2147483648, // -2^31
        max: 2147483647 // 2^31 - 1
    });

    const handleStopSequencesChange = (value: string) => {
        // Convert comma-separated string to array
        if (value.trim()) {
            const sequences = value.split(',').map(s => s.trim()).filter(s => s);
            setParameters({
                ...parameters,
                stop_sequences: sequences.slice(0, 5) // Max 5 sequences
            });
        } else {
            setParameters({
                ...parameters,
                stop_sequences: []
            });
        }
    };

    const getStopSequencesValue = (): string => {
        if (Array.isArray(parameters.stop_sequences)) {
            return parameters.stop_sequences.join(', ');
        }
        return '';
    };

    const InfoIcon: React.FC<{ content: string }> = ({
        content
    }) => (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                    <p className="text-xs">{content}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const renderFieldWithSlider = (
        id: string,
        label: string,
        tooltip: string,
        value: number,
        onChange: (val: number) => void,
        min: number,
        max: number,
        step: number
    ) => (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Label htmlFor={id} className="text-sm">{label}</Label>
                    <InfoIcon content={tooltip} />
                </div>
                <Input
                    id={id}
                    type="number"
                    value={value}
                    step={step}
                    max={max}
                    min={min}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="h-8 w-20"
                />
            </div>
            <Slider
                id={`${id}-slider`}
                value={[value]}
                onValueChange={(val) => onChange(Number(val))}
                step={step}
                max={max}
                min={min}
                className="w-full"
            />
        </div>
    );

    return (
        <Popover>
            <PopoverTrigger asChild>
                <div className=''>
                    <Button aria-haspopup="true" size="icon" variant="ghost">
                        <Settings2 className="h-5 w-5" />
                    </Button>
                </div>
            </PopoverTrigger>

            <PopoverContent className="w-[680px] select-none max-h-[600px] overflow-y-auto" align="center">
                <div className="grid gap-4">
                    <div className="space-y-2">
                        <h4 className="font-medium leading-none text-sm">Configure Gemini Model Parameters</h4>
                        <p className="text-xs text-muted-foreground">
                            Set the parameters for the selected Gemini model.
                        </p>
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        {/* Column 1 */}
                        <div className="space-y-4">
                            {/* Max Output Tokens */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="maxOutputTokens" className="text-sm">Max Output Tokens</Label>
                                    <InfoIcon content="Maximum number of tokens to generate. Controls the length of the output. Higher values allow longer responses. Model-dependent (e.g., 8192 for Gemini 1.5)." />
                                </div>
                                <Input
                                    id="maxOutputTokens"
                                    type="number"
                                    value={parameters.max_output_tokens}
                                    step={1}
                                    min={1}
                                    max={8192}
                                    onChange={(e) => {
                                        setParameters({
                                            ...parameters,
                                            max_output_tokens: Number(e.target.value)
                                        });
                                    }}
                                    className="h-8"
                                />
                            </div>

                            {/* Temperature */}
                            {renderFieldWithSlider(
                                'temp',
                                'Temperature',
                                'Controls randomness in output. Lower values (e.g., 0.2) make output more focused and deterministic. Higher values (e.g., 1.5) make it more creative and diverse.',
                                parameters.temperature,
                                (val) => setParameters({
                                    ...parameters,
                                    temperature: val
                                }),
                                tempLimit.min,
                                tempLimit.max,
                                0.1
                            )}

                            {/* Top P */}
                            {renderFieldWithSlider(
                                'topP',
                                'Top P',
                                'Nucleus sampling. The model considers only the most probable tokens with probabilities that add up to this value. Lower values (e.g., 0.1) make output more focused, higher values (e.g., 0.9) more diverse.',
                                parameters.top_p,
                                (val) => setParameters({
                                    ...parameters,
                                    top_p: val
                                }),
                                topPLimit.min,
                                topPLimit.max,
                                0.1
                            )}

                            {/* Top K */}
                            {renderFieldWithSlider(
                                'topK',
                                'Top K',
                                'Top-K sampling. The model considers only the top K most probable tokens for the next token. Lower values make output more focused, higher values more diverse.',
                                parameters.top_k,
                                (val) => setParameters({
                                    ...parameters,
                                    top_k: Math.round(val)
                                }),
                                topKLimit.min,
                                topKLimit.max,
                                1
                            )}

                            {/* Frequency Penalty */}
                            {renderFieldWithSlider(
                                'frequencyPenalty',
                                'Frequency Penalty',
                                'Reduces repetition by penalizing tokens based on how often they appear. Positive values discourage repetition, negative values encourage it.',
                                parameters.frequency_penalty,
                                (val) => setParameters({
                                    ...parameters,
                                    frequency_penalty: val
                                }),
                                frequencyPenaltyLimit.min,
                                frequencyPenaltyLimit.max,
                                0.1
                            )}
                        </div>

                        {/* Column 2 */}
                        <div className="space-y-4">
                            {/* Presence Penalty */}
                            {renderFieldWithSlider(
                                'presencePenalty',
                                'Presence Penalty',
                                'Encourages the model to talk about new topics by penalizing tokens that have already appeared. Positive values increase topic diversity.',
                                parameters.presence_penalty,
                                (val) => setParameters({
                                    ...parameters,
                                    presence_penalty: val
                                }),
                                presencePenaltyLimit.min,
                                presencePenaltyLimit.max,
                                0.1
                            )}

                            {/* Candidate Count */}
                            {renderFieldWithSlider(
                                'candidateCount',
                                'Candidate Count',
                                'The number of candidate responses to generate. The model will generate multiple responses and you can choose the best one. Maximum of 8 candidates.',
                                parameters.candidate_count,
                                (val) => setParameters({
                                    ...parameters,
                                    candidate_count: Math.round(val)
                                }),
                                candidateCountLimit.min,
                                candidateCountLimit.max,
                                1
                            )}

                            {/* Stop Sequences */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="stopSequences" className="text-sm">Stop Sequences</Label>
                                    <InfoIcon content="Sequences where the model will stop generating. Maximum of 5 sequences. Enter multiple sequences separated by commas (e.g., '\n\n', '</END>')." />
                                </div>
                                <Input
                                    id="stopSequences"
                                    type="text"
                                    value={getStopSequencesValue()}
                                    onChange={(e) => handleStopSequencesChange(e.target.value)}
                                    placeholder="e.g., \n\n, </END>"
                                    className="h-8"
                                />
                            </div>

                            {/* Seed */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="seed" className="text-sm">Seed (optional)</Label>
                                    <InfoIcon content="Sets a random seed for deterministic output. Using the same seed with identical inputs will produce the same output. Leave empty for random generation." />
                                </div>
                                <Input
                                    id="seed"
                                    type="number"
                                    value={parameters.seed || ''}
                                    step={1}
                                    max={seedLimit.max}
                                    min={seedLimit.min}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setParameters({
                                            ...parameters,
                                            seed: value ? Number(value) : undefined
                                        });
                                    }}
                                    placeholder="None"
                                    className="h-8"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default GeminiConfiguration;
